import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import useAppSelector from '@hooks/useAppSelector';
import { taskStatusTypes } from '@modules/cap/TicketList/constants/ticketlist.constants';
import { ConditionalRendering } from '../../components/common/conditional-rendering';

export interface IQuickActionBarTicketData {
    status?: string;
    ticketStatus?: taskStatusTypes;
    assignee?: string;
    currentTask?: string;
}

export interface ITicketQuickActionBarOwnProps {
    ticketData: IQuickActionBarTicketData;
    handleChange?: React.Dispatch<React.SetStateAction<IQuickActionBarTicketData>>;
}

const CURRENT_TASK_VISIBLE_STATUSES: readonly taskStatusTypes[] = [
    taskStatusTypes.OPEN,
    taskStatusTypes.UNCLAIMED,
    taskStatusTypes.RESTARTED,
    taskStatusTypes.REOPENED,
];

const StyledPill = styled.span`
    display: inline-flex;
    align-items: center;
    margin-left: 8px;
    padding: 6px 14px;
    border-radius: 20px;
    font-weight: 600;
    background-color: ${({ theme }) => theme.warningLight};
    color: ${({ theme }) => theme.warningDarkest};
`;

export type TicketQuickActionBarProps = ITicketQuickActionBarOwnProps;

const TicketQuickActionBar = (props: TicketQuickActionBarProps) => {
    const { ticketData } = props;
    const { isConfidential, loaded: ticketDataLoaded } =
        useAppSelector(state => state.individualPortal.ticketSahInfo) ?? {};
    const showCurrentTask =
        ticketData.status != null &&
        CURRENT_TASK_VISIBLE_STATUSES.includes(ticketData.status as taskStatusTypes);

    return (
        <table className="table table-striped" data-automation-id="tbl_ticket_mgmt_quick_action_bar">
            <tbody>
                <tr>
                    <td>
                        <strong>
                            <FormattedMessage id="ticket_management_ticket_status" />
                        </strong>
                        <FormattedMessage
                            id="ticket_management_non_inter_text"
                            values={{ text: ticketData.ticketStatus }}
                        />
                        <ConditionalRendering isTrue={ticketDataLoaded && isConfidential}>
                            <StyledPill>
                                <FormattedMessage id="sidenav.confidential.label" />
                            </StyledPill>
                        </ConditionalRendering>
                    </td>

                    <ConditionalRendering isTrue={!!ticketData.assignee}>
                        <td>
                            <strong>
                                <FormattedMessage id="ticket_management_assignee" />
                            </strong>
                            <FormattedMessage
                                id="ticket_management_non_inter_text"
                                values={{ text: ticketData.assignee }}
                            />
                        </td>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={showCurrentTask}>
                        <td>
                            <strong>
                                <FormattedMessage id="ticket_management_ticket_current_task" />
                            </strong>
                            <FormattedMessage
                                id="ticket_management_non_inter_text"
                                values={{ text: ticketData.currentTask }}
                            />
                        </td>
                    </ConditionalRendering>

                    <ConditionalRendering isTrue={ticketData.ticketStatus === taskStatusTypes.CLAIMED}>
                        <>
                            <td></td>
                            <td></td>
                            <td></td>
                        </>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticketData.ticketStatus === taskStatusTypes.UNCLAIMED}>
                        <td></td>
                    </ConditionalRendering>
                    <ConditionalRendering isTrue={ticketData.ticketStatus !== taskStatusTypes.CLAIMED &&
                            ticketData.status !== taskStatusTypes.UNCLAIMED}>
                        <td></td>
                    </ConditionalRendering>
                </tr>
            </tbody>
        </table>
    );
};

export default TicketQuickActionBar;
